import React, { Dispatch } from 'react';

import downloadLargeTokens from '@amzn/meridian-tokens/base/icon/download-large';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import { isEmpty } from 'lodash';
import { FilterInput } from 'src/components/FilterInput';
import { ServiceId } from 'src/constants';
import { useExportCoreRates } from 'src/hooks/useExportCoreRates';
import { IMetadata } from 'src/types/IMetadata';
import { IPrimeAMFRateCardValidationAction } from 'src/types/IPrimeAMFRateCardValidationAction';
import { IPrimeAMFRateCardValuesAction } from 'src/types/IPrimeAMFRateCardValuesAction';
import { IPrimeAMFRateCardValues } from 'src/types/IRateCard';

import { IPrimeAMFRateCardErrors } from '../rateCardValidationReducer';
import { AddCppGrid } from './AddCppGrid';
import { AddMauGrid } from './AddMauGrid';

interface IAddCoreRatesProps {
    rateCardValues: IPrimeAMFRateCardValues;
    dispatchRateCardValues: Dispatch<IPrimeAMFRateCardValuesAction>;
    rateCardErrors: IPrimeAMFRateCardErrors;
    dispatchRateCardErrors: Dispatch<IPrimeAMFRateCardValidationAction>;
    augmentedMetadata: IMetadata;
}

export const AddCoreRates = ({
    rateCardValues,
    dispatchRateCardValues,
    rateCardErrors,
    dispatchRateCardErrors,
    augmentedMetadata
}: IAddCoreRatesProps) => {
    const isPrimeRateCard = rateCardValues.serviceId === ServiceId.Prime;
    const { filterText, setFilterText, setCppGridApi, setMauGridApi, exportCoreRates } = useExportCoreRates();
    const hasCppTerms = isPrimeRateCard
        ? !(
              isEmpty(rateCardValues.primeContractTerms?.costPerPlay?.conversions) &&
              isEmpty(rateCardValues.primeContractTerms?.costPerPlay?.customerTypes) &&
              isEmpty(rateCardValues.primeContractTerms?.costPerPlay?.playTypes) &&
              isEmpty(rateCardValues.primeContractTerms?.costPerPlay?.programTypeList)
          )
        : !(
              isEmpty(rateCardValues.amfContractTerms?.adRevShare?.conversions) &&
              isEmpty(rateCardValues.amfContractTerms?.adRevShare?.playTypes) &&
              isEmpty(rateCardValues.amfContractTerms?.adRevShare?.revSharePercent)
          );
    const hasMauTerms = !isEmpty(rateCardValues.primeContractTerms?.monthlyActiveUser?.customerType);

    return (
        <Column heights={['fit', 'fill', 'fill']}>
            <Box type="fill" spacingInset="400">
                <Row alignmentHorizontal="justify">
                    <FilterInput filterText={filterText} setFilterText={setFilterText} />
                    <Button type="link" size="small" onClick={exportCoreRates}>
                        <Icon tokens={downloadLargeTokens} />
                        Download
                    </Button>
                </Row>
            </Box>
            {hasCppTerms && (
                <AddCppGrid
                    rateCardValues={rateCardValues}
                    dispatchRateCardValues={dispatchRateCardValues}
                    rateCardErrors={rateCardErrors}
                    dispatchRateCardErrors={dispatchRateCardErrors}
                    filterText={filterText}
                    setCppGridApi={setCppGridApi}
                    augmentedMetadata={augmentedMetadata}
                />
            )}
            {hasMauTerms && (
                <AddMauGrid
                    rateCardValues={rateCardValues}
                    dispatchRateCardValues={dispatchRateCardValues}
                    rateCardErrors={rateCardErrors}
                    dispatchRateCardErrors={dispatchRateCardErrors}
                    filterText={filterText}
                    setMauGridApi={setMauGridApi}
                    augmentedMetadata={augmentedMetadata}
                />
            )}
        </Column>
    );
};
